import {
  CreateWebWorkerMLCEngine,
  prebuiltAppConfig,
} from '@mlc-ai/web-llm';

const MODEL_PREFERENCES = [
  'qwen2.5-0.5b-instruct',
  'qwen2-0.5b-instruct',
  'llama-3.2-1b-instruct',
  'phi-3.5-mini-instruct',
  'gemma-2-2b-it',
];

const progressSubscribers = new Set();

let enginePromise = null;
let workerInstance = null;
let selectedModelId = null;
let preloadScheduled = false;

function emitProgress(progress) {
  progressSubscribers.forEach((subscriber) => subscriber(progress));
}

function getAvailableModels() {
  return prebuiltAppConfig?.model_list ?? [];
}

function resolvePreferredModel() {
  const models = getAvailableModels();
  if (models.length === 0) {
    throw new Error('WebLLM did not expose any prebuilt models.');
  }

  const preferredRecord = MODEL_PREFERENCES
    .map((needle) =>
      models.find((record) =>
        String(record.model_id || '').toLowerCase().includes(needle),
      ),
    )
    .find(Boolean);

  return preferredRecord?.model_id ?? models[0].model_id;
}

function getWorker() {
  if (!workerInstance) {
    workerInstance = new Worker(new URL('./webllm.worker.js', import.meta.url), {
      type: 'module',
    });
  }
  return workerInstance;
}

function isWebGPUSupported() {
  return typeof navigator !== 'undefined' && typeof navigator.gpu !== 'undefined';
}

export function subscribeToWebLLMProgress(callback) {
  progressSubscribers.add(callback);
  return () => {
    progressSubscribers.delete(callback);
  };
}

export function getWebLLMModelLabel() {
  return selectedModelId;
}

export function canUseWebLLM() {
  return typeof window !== 'undefined' && typeof Worker !== 'undefined' && isWebGPUSupported();
}

export async function ensureWebLLMEngine() {
  if (!canUseWebLLM()) {
    throw new Error(
      'This browser does not support WebGPU yet. Please use a recent Chrome, Edge, or another WebGPU-enabled browser.',
    );
  }

  if (!enginePromise) {
    selectedModelId = resolvePreferredModel();
    const appConfig = {
      ...prebuiltAppConfig,
      cacheBackend: 'indexeddb',
    };

    enginePromise = CreateWebWorkerMLCEngine(
      getWorker(),
      selectedModelId,
      {
        appConfig,
        initProgressCallback: emitProgress,
      },
    ).catch((error) => {
      enginePromise = null;
      throw error;
    });
  }

  return enginePromise;
}

export function preloadWebLLMEngine() {
  if (preloadScheduled || !canUseWebLLM()) {
    return;
  }

  preloadScheduled = true;

  const startPreload = () => {
    void ensureWebLLMEngine().catch(() => {
      preloadScheduled = false;
    });
  };

  if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(startPreload, { timeout: 2000 });
    return;
  }

  setTimeout(startPreload, 0);
}

function normalizeAssistantContent(content) {
  if (typeof content === 'string') {
    return content.trim();
  }

  if (Array.isArray(content)) {
    return content
      .map((part) => {
        if (typeof part === 'string') {
          return part;
        }
        if (part && typeof part.text === 'string') {
          return part.text;
        }
        return '';
      })
      .join('')
      .trim();
  }

  return '';
}

export async function generateWebLLMReply({
  messages,
  onChunk,
  temperature = 0.4,
  topP = 0.9,
  maxTokens = 320,
}) {
  const engine = await ensureWebLLMEngine();
  const request = {
    messages,
    temperature,
    top_p: topP,
    max_tokens: maxTokens,
  };

  try {
    let reply = '';
    const stream = await engine.chat.completions.create({
      ...request,
      stream: true,
      stream_options: { include_usage: true },
    });

    for await (const chunk of stream) {
      const delta = chunk.choices?.[0]?.delta?.content || '';
      if (!delta) {
        continue;
      }

      reply += delta;
      if (onChunk) {
        onChunk(reply);
      }
    }

    return reply.trim();
  } catch (streamError) {
    try {
      const completion = await engine.chat.completions.create(request);
      const reply = normalizeAssistantContent(completion?.choices?.[0]?.message?.content);
      if (reply && onChunk) {
        onChunk(reply);
      }
      return reply;
    } catch (nonStreamingError) {
      const streamMessage = streamError instanceof Error ? streamError.message : String(streamError || '');
      const fallbackMessage = nonStreamingError instanceof Error ? nonStreamingError.message : String(nonStreamingError || '');
      throw new Error(
        [streamMessage, fallbackMessage]
          .filter(Boolean)
          .join(' | ')
          || 'WebLLM failed to generate a response.',
      );
    }
  }
}
